import { HiExclamationTriangle } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import { Button } from "flowbite-react";
import { useIntegrations } from "../../contexts/IntegrationsContext";
import { integrationPath } from "../../routes";
import { INTEGRATION_CATEGORIES } from "../../pages/Integrations/catalog";

// Derived from the catalog the Integrations page actually routes by, NOT hand-maintained.
// The page falls back to its first connected provider (Slack) for any id it does not know,
// so a banner naming tl;dv or Jiminny — or a multi-fault banner with no id at all — used to
// land the customer on an integration the banner never mentioned.
const CATALOG_SERVICES = INTEGRATION_CATEGORIES.flatMap((c) => c.services);
const ROUTABLE_IDS = new Set(CATALOG_SERVICES.map((s) => s.id));
const CATALOG_NAMES = Object.fromEntries(
  CATALOG_SERVICES.map((s) => [s.id, s.name]),
);

// Statuses that meant "broken" before the backend derived connection_health. Retained as a
// deploy-order fallback ONLY — see the note on needsReconnect below. Do not extend it; a new
// status gets a bucket on the backend.
const LEGACY_UNHEALTHY_STATUSES = ["EXPIRED", "INVALID", "ERROR"];

// How long an unfinished setup may sit before we call it out. Someone who is mid-wizard
// right now does not need a banner telling them they are mid-wizard.
const SETUP_GRACE_MS = 60 * 60 * 1000;

// Only integrations with a guided setup can be told to "finish connecting". Amplitude,
// Mixpanel and Slack have no wizard and no path out of PENDING_SETUP in the product today,
// so every one of them would raise a permanent banner whose button leads nowhere useful.
const HAS_SETUP_FLOW = ["hubspot"];

// Integrations with no settings pane of their own — backend-only, reachable by the auth
// webhook (which has no provider filter) and therefore able to raise this banner.
// Capitalisation matters: title-casing gives "Tldv", and the product is "tl;dv".
const OFF_CATALOG_NAMES = {
  tldv: "tl;dv",
  jiminny: "Jiminny",
  segment: "Segment",
  pendo: "Pendo",
  generic: "Usage data",
};

const displayName = (id) =>
  CATALOG_NAMES[id] ??
  OFF_CATALOG_NAMES[id] ??
  (id ? id.charAt(0).toUpperCase() + id.slice(1) : "");

const label = (names) =>
  names.length === 1 ? names[0] : `${names.length} integrations`;

const needsReconnect = (integration) =>
  integration.connection_health
    ? integration.connection_health === "needs_reconnect"
    : // No connection_health means an API that predates it. For THIS bucket a working
      // predicate already existed, so falling silent would delete the broken-connection
      // banner during a frontend-first deploy — reintroducing the incident's blind spot on
      // the reconnect path while closing it on the setup path.
      LEGACY_UNHEALTHY_STATUSES.includes(integration.status);

const setupStalled = (integration) =>
  integration.connection_health === "setup_incomplete" &&
  HAS_SETUP_FLOW.includes(integration.integration_id) &&
  // Missing timestamp => treat as in-progress and stay quiet. Overstating silence is
  // recoverable; a false alarm on every fresh connect is not.
  Boolean(integration.status_changed_at) &&
  Date.now() - new Date(integration.status_changed_at).getTime() >
    SETUP_GRACE_MS;

/**
 * Banner shown when an integration needs the customer to do something.
 *
 * Reads the backend-derived `connection_health` (PAN-258) rather than mirroring the status
 * vocabulary. Two buckets get a banner, with distinct copy, because they need different
 * actions:
 *
 *   needs_reconnect  — auth broke. Reconnect. Falls back to the legacy status list so this
 *                      bucket survives a frontend-first deploy.
 *   setup_incomplete — connected but never configured, so nothing syncs. Finish setup.
 *                      This is the state that hid for six days on 2026-07-31: no banner at
 *                      all, and a green dot on the settings page.
 *
 * `disconnected` is silent — a deliberate disconnect is not a fault, and an unclosable
 * banner is one people learn to ignore.
 */
export const ConnectionAlert = () => {
  const { integrations, loading } = useIntegrations();
  const navigate = useNavigate();

  if (loading) return null;

  const broken = integrations.filter(needsReconnect);
  const stalled = integrations.filter(setupStalled);

  if (broken.length === 0 && stalled.length === 0) return null;

  const isBroken = broken.length > 0;
  const affected = isBroken ? broken : stalled;
  const names = affected
    .map((i) => displayName(i.integration_id))
    .filter(Boolean);
  // Send the customer to an integration the banner is actually about AND that the page can
  // render. Passing an unknown id — or no id at all, as the multi-fault case used to —
  // silently resolves to the page's default provider, which is Slack: an integration the
  // banner never mentioned. Where nothing is routable (tl;dv, Jiminny have no settings
  // pane), the banner still reports the fault but offers no misleading destination.
  const routable = affected.find((i) => ROUTABLE_IDS.has(i.integration_id));
  const destination = routable
    ? integrationPath(routable.integration_id)
    : null;

  return (
    <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <div className="flex items-center gap-3">
        <HiExclamationTriangle className="h-5 w-5 flex-shrink-0 text-amber-500" />
        <p className="text-sm text-amber-800">
          <span className="font-medium">{label(names)}</span>
          {isBroken
            ? " connection needs attention. Data sync has stopped."
            : " setup was never finished, so no data is syncing."}
        </p>
      </div>
      {destination && (
        <Button size="xs" color="warning" onClick={() => navigate(destination)}>
          {isBroken ? "Reconnect in Integrations" : "Finish connecting"}
        </Button>
      )}
    </div>
  );
};
